import React, { useCallback, useEffect, useState } from 'react';
import { ModelGroup, nameMap } from '../../../Controller/3DController/ModelLoad/ModelLoadController';
import { ConfiguratorModel } from '../../../Controller/3DController/ConfiguratorModel';
import { getWhiteLabel } from '../../../whitelabel';
import { AR, ChevronLeft, ChevronRight, Close, CubeAR, Gyro, Mobile } from '../Icons/Icons';

import './ModelChange.scss';
import { translate } from '../../../Controller/3DController/translateController';

export interface ModelChangeProps {
    close: () => void;
    configuratorModel: ConfiguratorModel;
};

export const ModelChange: React.FC<ModelChangeProps> = ({
    close,
    configuratorModel,
    ...props
}) => {
    const [models, setModels] = useState<ModelGroup[]>(null);
    const [currentModelName, setCurrentModelName] = useState<string>('');
    const [currentModelIndex, setCurrentModelIndex] = useState<number>(undefined);
    const [modelsLength, setModelsLength] = useState<number>(0);
    const [modelGroupIndexes, setModelGroupIndexes] = useState<number[]>(null);
    const [bgColorHsl, setBgColorHsl] = useState<number[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isLandscape, setLandscape] = useState<boolean>(false);
    const [touchPosition, setTouchPosition] = useState<number>(null);
    const [transitionEnabled, setTransitionEnabled] = useState<boolean>(true);
    const [isSwitching, setSwitching] = useState<boolean>(false);
    const [isModelSelected, setModelSelected] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<number>(undefined);
    const [firstOpen, setFirstOpen] = useState<boolean>(true);

    useEffect(() => {
        const modelLoadController = configuratorModel.modelLoadController;
        const modelGroupList = modelLoadController.modelGroupList;
        let currentModelGroup = modelLoadController.getCurrentModelGroup();
        let currentModelName = modelLoadController.currentModelName;
        
        let currentModel = 0;
        let newModelGroupIndexes: number[] = [];

        if (currentModelName !== '' && !getWhiteLabel().hiddenCars.includes(currentModelName)) {
            currentModel = modelLoadController.getModelGroupIndex(currentModelName);
            setSelectedModel(currentModel);
            setModelSelected(true);
            setTransitionEnabled(false);

            setTimeout(() => {
                setTransitionEnabled(true);
                setSelectedModel(undefined);
            }, 100);
        }

        modelGroupList.map((item) => {
            if (item && currentModelGroup && item.name === currentModelGroup.name) {
                newModelGroupIndexes.push(item.cars.indexOf(currentModelName));
            } else {
                newModelGroupIndexes.push(0);
            }
        });

        setCurrentModelIndex(currentModel);
        setModels(modelGroupList);
        setModelsLength(modelGroupList.length);
        setModelGroupIndexes(newModelGroupIndexes);

        checkOrientation();

        window.addEventListener('resize', checkOrientation);

        return () => {
            window.removeEventListener('resize', checkOrientation);
        }
    }, []);

    useEffect(() => {
        if (currentModelIndex || currentModelIndex === 0) {
            if (currentModelIndex === modelsLength - 1 && currentModelIndex !== selectedModel) {
                setTransitionEnabled(true);
            }

            let modelIndex = currentModelIndex;

            if (currentModelIndex <= -1) {
                modelIndex = modelsLength - 1;
            } else if (currentModelIndex >= modelsLength) {
                modelIndex = 0;
            }

            if (currentModelIndex === 0) {
                setSwitching(true);
                setTimeout(() => {
                    setSwitching(false);
                    setTransitionEnabled(true);
                }, 500);
            }

            setCurrentModelName(models[modelIndex].name);

            (async () => {
                const bgColors = await configuratorModel.modelLoadController.getDefaultColor(models[modelIndex].cars[modelGroupIndexes[modelIndex]]);

                setBgColorHsl(rgbToHsl(bgColors[0], bgColors[1], bgColors[2]));
            })();
        }
    }, [currentModelIndex, modelGroupIndexes]);

    const onVariantChange = (updatedModelGroupIndexes: number[]) => {
        if (firstOpen) setFirstOpen(false);

        setModelGroupIndexes(updatedModelGroupIndexes);
    };

    const onNextSlide = () => {
        if (isSwitching) return;

        if (firstOpen) setFirstOpen(false);

        if (currentModelIndex < modelsLength) {
            setCurrentModelIndex(prevState => prevState + 1);
            setSwitching(true);
        }
    };

    const onPreviousSlide = () => {
        if (isSwitching) return;

        if (firstOpen) setFirstOpen(false);

        if (currentModelIndex > -1) {
            setCurrentModelIndex(prevState => prevState - 1);
            setSwitching(true);
        }
    };

    const onClose = () => {
        if (isLoading || isSwitching || !isModelSelected) return;

        close();
    }

    const handleTransitionEnd = () => {
        if (currentModelIndex === -1) {
            setTransitionEnabled(false);
            setCurrentModelIndex(modelsLength - 1);
        } else if (currentModelIndex === modelsLength) {
            setTransitionEnabled(false);
            setCurrentModelIndex(0);
        }

        setSwitching(false);
    };

    const handleTouchStart = (e) => {
        const touchDown = e.touches[0].clientX;
        setTouchPosition(touchDown);
    };

    const handleTouchMove = (e) => {
        const touchDown = touchPosition;

        if (touchDown === null) return;

        const currentTouch = e.touches[0].clientX;
        const diff = touchDown - currentTouch;

        if (diff > 10) onNextSlide();
        if (diff < -10) onPreviousSlide();
        
        setTouchPosition(null);
    };

    const onLoadModel = () => {
        if (isSwitching) return;
        
        const model = models[currentModelIndex].cars[modelGroupIndexes[currentModelIndex]];

        if (configuratorModel.modelLoadController.checkModelActive(model)) {
            close();
            return;
        }

        setLoading(true);

        configuratorModel.modelLoadController.loadModel(model)
            .then(() => {
                setLoading(false);
                setModelSelected(true);
                close();
            })
            .catch(() => setLoading(false));
    };

    const renderDisclaimerText = () => {
        if (!models) return null;
        let currentModel = currentModelIndex;

        if (currentModelIndex < 0) {
            currentModel = modelsLength - 1;
        } else if (currentModelIndex >= modelsLength) {
            currentModel = 0;
        }

        const modelData = configuratorModel.modelLoadController.getModelByCode(
            models[currentModel].cars[modelGroupIndexes[currentModel]]
        );

        return modelData ? modelData.disclaimer : null;
    };

    const checkOrientation = () => {
        if (window.matchMedia('(orientation: landscape)').matches) {
            setLandscape(true);
        } else {
            setLandscape(false);
        }
    };

    const renderExtraPrevious = () => {
        if (!models) return;
        const output = [];

        for (let i = 0; i < 1; i++) {
            output.push(models[modelsLength - 1 - i]);
        }

        return renderModel(output[0], -1);
    };

    const renderExtraNext = () => {
        if (!models) return;
        const output = [];

        for (let i = 0; i < 1; i++) {
            output.push(models[i]);
        }

        return renderModel(output[0], modelsLength);
    };

    const rgbToHsl = (r: number, g: number, b: number): number[] => {
        r /= 255, g /= 255, b /= 255;
        var max = Math.max(r, g, b), min = Math.min(r, g, b);
        var h, s, l = (max + min) / 2;

        if(max == min){
            h = s = 0;
        }else{
            var d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch(max){
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }

        return [Math.floor(h * 360), Math.floor(s * 100), Math.floor(l * 100)];
    };

    const renderModel = (model, modelIndex) => {
        const modelGroupIndex = models.indexOf(model);
        const picture = `./${HASH}/options/new/${model.cars[modelGroupIndexes[modelGroupIndex]]}${isLandscape ? '-side' : ''}.png`;
        const isNew = model.name.toLowerCase().indexOf('nuevo') === 0 ? true : false;
        const carName = isNew ? model.name.replace('Nuevo', '') : model.name;
        return (
            <div
                key={`model_${modelIndex}`}
                className={`vw-model-change__carousel-content-item ${(modelIndex !== currentModelIndex) ? `inactive` : `active`}`}
            >
                <div className="vw-model-change__model-name">
                    <div className="vw-model-change__model-name-new">{isNew ? 'Nuevo' : '\u00A0'}</div>
                    <div className="vw-model-change__model-name-model">{carName}</div>

                    {model.cars && model.cars.length > 1 && (
                        <div className="vw-model-change__model-variants">
                            {model.cars.map((variant, variantIndex) => {
                                const variantData = nameMap.find(e => e.car === variant);

                                return (
                                    <div
                                        key={`${model}-variant-${variantIndex}`}
                                        className={`vw-model-change__model-variants__variant ${variantIndex === modelGroupIndexes[modelGroupIndex] ? 'active' : ''}`}
                                        onClick={() => {
                                            let indexes: number[] = [...modelGroupIndexes];
                                            indexes[modelGroupIndex] = variantIndex;
                                            const updatedModelGroupIndexes = indexes.slice();

                                            onVariantChange(updatedModelGroupIndexes)
                                        }}
                                    >
                                        {variantData.line || variantData.name}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                <img className="item-img" src={picture} />
            </div>
        );
    };

    return (
        <div
            className="vw-model-change"
            style={{
                backgroundColor: `hsl(
                    ${bgColorHsl[0] < 10 ? `${bgColorHsl[0] - 10}` : bgColorHsl[0]},
                    ${bgColorHsl[1]}%,
                    ${currentModelName === 'Teramont' || currentModelName == "Saveiro" ? '60%' : '40%'}
                )`,
                transition: firstOpen ? 'none' : undefined,
            }}
            {...props}
        >
            <div className="vw-model-change__dynamic-bg" />
            <div className="vw-model-change__carousel-container">
                <div
                    className="vw-model-change__carousel-wrapper"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                >
                    <div className="vw-model-change__carousel-content-wrapper">
                        <div
                            className="vw-model-change__carousel-content"
                            style={{
                                transition: !transitionEnabled ? 'none' : undefined,
                                transform: `translateX(${currentModelIndex === 0 ? `-100%` : `-${currentModelIndex * 100 + 100}%`})`,
                            }}
                            onTransitionEnd={handleTransitionEnd}
                        >
                            {renderExtraPrevious()}
                            {models && models.map((model, modelIndex) => renderModel(model, modelIndex))}
                            {renderExtraNext()}
                        </div>
                    </div>
                </div>
            </div>

            <div className="vw-model-change__action-wrapper">
                <div
                    className="vw-model-change__start-button"
                    onClick={onLoadModel}
                >
                    {isLoading ? translate('id_selected') : translate('id_select')}
                </div>

                <div className="vw-model-change__disclaimer-text">
                    {renderDisclaimerText()}
                </div>
            </div>

            <div className="vw-model-change__bottom-nav">
                <div
                    className="vw-model-change__button transparent"
                    onClick={onPreviousSlide}
                >
                    <ChevronLeft />
                </div>

                <div
                    className="vw-model-change__button"
                    onClick={() => {
                        onClose();
                    }}
                >
                    <Close />
                </div>

                <div
                    className="vw-model-change__button transparent"
                    onClick={onNextSlide}
                >
                    <ChevronRight />
                </div>
            </div>

            {isLoading && (
                <div className="vw-model-change__loading">
                    <div className="vw-model-change__loading-icon">
                        <CubeAR />
                    </div>
                    <div className="vw-model-change__loading-text">{translate('Starting Experience...')}</div>
                </div>
            )}
        </div>
    );
};

export default ModelChange;
